package com.eladfinish.bible.contest.features.hebrewCalendar

import io.github.aakira.napier.Napier
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus
import sternbach.software.kosherkotlin.hebrewcalendar.HebrewDateFormatter
import sternbach.software.kosherkotlin.hebrewcalendar.JewishCalendar
import sternbach.software.kosherkotlin.hebrewcalendar.JewishCalendar.Companion.toJewishDayOfWeek

/**
 * What is read on one Shabbat: the Hebrew name the calendar library gives it, and the date of that
 * Shabbat. [name] is a holiday's name when the Shabbat has no parasha of its own.
 */
data class UpcomingParasha(
    val name: String,
    val shabbat: LocalDate,
)

object ParashaCalendar {

    /**
     * The parasha of the week of [today]: the one read on the next Shabbat, [today] itself when it
     * is Shabbat. [week] jumps that many weeks forward.
     */
    fun upcoming(
        today: LocalDate,
        week: Int = 0,
    ): UpcomingParasha {
        val jewishCalendar = JewishCalendar().apply {
            inIsrael = true // TODO: check if user is in Israel
            setGregorianDate(
                year = today.year,
                month = today.month.number,
                dayOfMonth = today.day,
            )
        }

        // Get the day of week (1-7, where 1 = Sunday, 7 = Saturday)
        val currentDayOfWeek = jewishCalendar.gregorianLocalDate.dayOfWeek.toJewishDayOfWeek()

        // Days to add to reach the desired Shabbat:
        // - week * 7: jump forward by the specified number of weeks
        // - (7 - currentDayOfWeek): days remaining until the next Shabbat
        val daysToAdd = week * 7 + (7 - currentDayOfWeek)
        val targetDate = jewishCalendar.gregorianLocalDate.plus(daysToAdd, DateTimeUnit.DAY)

        jewishCalendar.setGregorianDate(
            year = targetDate.year,
            month = targetDate.month.number,
            dayOfMonth = targetDate.day,
        )

        val hebrewDateFormatter = HebrewDateFormatter().apply {
            isHebrewFormat = true // Use Hebrew format (not transliterated)
            isUseLongHebrewYears = true // Use long year format (e.g., תשפ"ה instead of פ"ה)
        }

        val parashaName = hebrewDateFormatter.formatParsha(jewishCalendar)
        Napier.d("getParasha - jewishCalendar $jewishCalendar, parashaName $parashaName")

        // No parasha (e.g., during Chol HaMoed): the holiday's name instead
        val name = if (parashaName.isNullOrBlank()) {
            hebrewDateFormatter.formatYomTov(jewishCalendar).also {
                Napier.d("getParasha - specialParashaName $it")
            }
        } else {
            parashaName
        }
        return UpcomingParasha(name = name, shabbat = targetDate)
    }
}
